import { useCallback, useEffect, useSyncExternalStore } from "react";

// Light / dark theme. The `dark` class on <html> drives the overrides in
// index.css (and Tailwind's `dark:` variant).
// Default = the browser / OS preference (prefers-color-scheme), followed live.
// Once the user clicks the toggle their choice is saved in sessionStorage and wins
// over the OS setting for the life of this tab.
// THEME_KEY is also read by the pre-paint script in index.html.
export const THEME_KEY = "theme";
const SYSTEM_DARK = "(prefers-color-scheme: dark)";
const listeners = new Set();

function savedTheme() {
  try {
    const v = window.sessionStorage.getItem(THEME_KEY);
    return v === "dark" || v === "light" ? v : null;
  } catch {
    return null;
  }
}

function systemTheme() {
  return window.matchMedia?.(SYSTEM_DARK).matches ? "dark" : "light";
}

function readTheme() {
  return savedTheme() ?? systemTheme();
}

function applyTheme(theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

function subscribe(cb) {
  listeners.add(cb);
  // OS theme changed - only matters while the user has not picked one.
  const mql = window.matchMedia?.(SYSTEM_DARK);
  const onSystem = () => {
    if (savedTheme()) return;
    applyTheme(readTheme());
    cb();
  };
  mql?.addEventListener("change", onSystem);
  return () => {
    listeners.delete(cb);
    mql?.removeEventListener("change", onSystem);
  };
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, readTheme, () => "light");

  useEffect(() => applyTheme(theme), [theme]);

  const setTheme = useCallback((next) => {
    try {
      window.sessionStorage.setItem(THEME_KEY, next);
    } catch {
      // storage blocked - theme still applies for this page view
    }
    applyTheme(next);
    listeners.forEach((l) => l());
  }, []);

  const toggleTheme = useCallback(
    () => setTheme(readTheme() === "dark" ? "light" : "dark"),
    [setTheme],
  );

  return { theme, isDark: theme === "dark", setTheme, toggleTheme };
}
